import {
  Mapping,
  NO_GROUP_ID,
  UNKNOWN_ID,
  UnsafeRecord,
  defaultCalendarDate,
  getDateIntervalsForStudentSetGroups,
  isDefined,
  mergeById,
} from "@timeedit/registration-shared";
import { Modal } from "@timeedit/ui-components";
import cn from "classnames";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  CalendarProps,
  Components,
  EventPropGetter,
  Formats,
  Views,
  dayjsLocalizer,
} from "react-big-calendar";
import "../../../react-big-calendar-layer.css";
import { CalendarEvent, createEvents } from "../../../utils/events";
import {
  LoadActions,
  RegistrationRequiredTranslations,
  TAnalyticsEvent,
} from "../types";
import {
  SelectOptions,
  limitedToRange,
  sortEventsOnGroupNumber,
} from "../utils";
import { EventWrapper, EventWrapperProps } from "./CalendarEvents/EventWrapper";
import { CalendarHeader } from "./CalendarHeader/CalendarHeader";
import { EventModalContentProps } from "./CalendarModalContent/GroupModalContent";
import {
  CalendarToolbar,
  DisplayState,
} from "./CalendarToolbar/CalendarToolbar";
import { useEventModal, useWeekendEventDetection } from "./hooks";
import "./registrationCalendar.scss";

import {
  AllocationStatus,
  FrontendRegistration,
  RegistrationMode,
  ScheduleEvent,
} from "@timeedit/registration-schemas";
import "../../../utils/dayjsSetup";

const calendarLocalizer = dayjsLocalizer(dayjs);

const defaultHourDate = "1970-01-02T01:00:00";

/* eslint-disable-next-line no-console */
const originalError = console.error;
const { suppressError, unSuppressError } = errorSuppression(originalError);

interface MinMaxHours {
  minHours: number;
  maxHours: number;
}

interface RegistrationCalendarProps<EventType extends object>
  extends Omit<CalendarProps<EventType, object>, "localizer" | "events"> {
  extraEvents?: CalendarEvent[];
  extraReservations?: ScheduleEvent[];
  eventModalContent?: (props: EventModalContentProps) => React.JSX.Element;
  registration: FrontendRegistration;
  mapping: Mapping;
  translations: RegistrationRequiredTranslations;
  mode?: RegistrationMode;
  activityId?: number;
  title: React.JSX.Element;
  loadActions: LoadActions;
  analyticsEvent?: TAnalyticsEvent;
  /** Lock to the list view and hide the list/calendar switch. */
  listOnly?: boolean;
  isDesktop: boolean;
}

export default function RegistrationCalendar(
  props: RegistrationCalendarProps<CalendarEvent>
): React.JSX.Element {
  handleErrorSuppression();

  const {
    dayLayoutAlgorithm = "no-overlap",
    onNavigate = (): void => {
      return;
    },
    onView = (): void => {},
    extraEvents = [],
    extraReservations = [],
    views = { agenda: true, week: true, work_week: true },
    eventModalContent: createEventModalContent = (): React.JSX.Element => (
      <div></div>
    ),
    registration,
    mapping,
    translations,
    mode,
    activityId,
    analyticsEvent,
    listOnly = false,
    isDesktop,
  } = props;

  const eventModal = useEventModal();

  const [{ minHours, maxHours }, setMinMaxHours] = useState<MinMaxHours>({
    minHours: 8,
    maxHours: 18,
  });

  const dateInterval = useMemo(
    () =>
      getDateIntervalsForStudentSetGroups({
        registration,
        studentSetGroupIds:
          registration.activities[activityId ?? UNKNOWN_ID]?.children ?? [],
      }),
    [activityId, registration]
  );

  const initialDisplayState = {
    student: DisplayState.Text,
    teacher: DisplayState.Text,
  } satisfies Record<RegistrationMode, DisplayState>;

  const [displayState, setDisplayState] = useState(
    listOnly
      ? DisplayState.Text
      : isDefined(mode)
        ? initialDisplayState[mode]
        : DisplayState.Graphical
  );

  const [startIntervalDate, endIntervalDate] = intervalDates();
  const reservationEvents = eventReservations();
  const eventsWithinSpan = findEventsWithinSpan();

  const hasWeekendEvents = useWeekendEventDetection(eventsWithinSpan);

  const graphicalView: SelectOptions = useMemo(() => {
    return hasWeekendEvents ? Views.WEEK : Views.WORK_WEEK;
  }, [hasWeekendEvents]);

  const view = useMemo(
    () =>
      !listOnly && displayState === DisplayState.Graphical
        ? graphicalView
        : Views.AGENDA,
    [graphicalView, displayState, listOnly]
  );
  const uniqueTimeMatchedEvents = createUniqueEvents();
  const sortedEvents = useMemo(
    () =>
      sortEventsOnGroupNumber({
        events: uniqueTimeMatchedEvents,
        mapping,
        groups: registration.groups,
        reverse: true,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uniqueTimeMatchedEvents]
  );

  const selectedActivityEvents = useMemo(
    () =>
      isDefined(activityId)
        ? sortedEvents.filter((e) => {
            const groupId = registration.groups[e.data.groupId]?.parentId;
            if (!isDefined(groupId)) return false;
            return registration.activities[groupId]?.id === activityId;
          })
        : sortedEvents,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedEvents, activityId]
  );

  const eventsOverlapping = useMemo(
    () => createEventsOverlapping({ events: selectedActivityEvents }),
    [selectedActivityEvents]
  );

  const components = props.components ?? createDefaultComponents();
  const eventPropGetter =
    props.eventPropGetter ?? createDefaultEventPropGetter();
  const onSelectEvent = props.onSelectEvent ?? createDefaultOnSelectEvent();
  const formats = props.formats ?? createDefaultFormats();

  const eventsAndOverlapping = useMemo(() => {
    return isDefined(eventModal.modalInfo)
      ? [
          eventModal.modalInfo,
          ...(eventsOverlapping[eventModal.modalInfo.id] ?? []),
        ]
      : [];
  }, [eventModal.modalInfo, eventsOverlapping]);

  const eventsWithRelated = useMemo(() => {
    return eventsAndOverlapping.map((event) => {
      const groupId = event.data.groupId;
      const linkIds = registration.groups[groupId]?.links;

      return {
        event,
        relatedEvents: selectedActivityEvents.filter((e) => {
          const sameIdOrLinked =
            groupId === e.data.groupId || linkIds?.includes(e.data.groupId);
          return sameIdOrLinked && event.id !== e.id;
        }),
      };
    });
  }, [eventsAndOverlapping, registration.groups, selectedActivityEvents]);

  const eventModalContent = createEventModalContent({
    title: props.title,
    eventsWithRelated,
  });

  return (
    <>
      <Calendar<CalendarEvent, object>
        {...props}
        className={cn(
          "te-registration-calendar",
          {
            "te-registration-calendar--mobile": !isDesktop,
            "te-registration-calendar--list-only": listOnly,
          },
          props.className
        )}
        events={sortedEvents}
        dayLayoutAlgorithm={dayLayoutAlgorithm}
        onNavigate={onNavigate}
        date={defaultCalendarDate}
        components={components}
        toolbar={!listOnly}
        views={views}
        view={view}
        defaultView={view}
        onView={onView}
        min={createHourDate(minHours)}
        max={createHourDate(maxHours)}
        onSelectEvent={onSelectEvent}
        formats={formats}
        localizer={calendarLocalizer}
        eventPropGetter={eventPropGetter}
      />
      {/* Modal for every event */}
      <Modal
        className={cn("bigmodal", {
          teachermodal: mode === "teacher",
          studentmodal: mode !== "teacher",
          "bigmodal--mobile": !isDesktop,
        })}
        width={"auto"}
        open={eventModal.showModal}
        onCancel={() => eventModal.setShowModal(false)}
        footer={false}
      >
        {eventModalContent}
      </Modal>
    </>
  );

  function createDefaultEventPropGetter(): EventPropGetter<CalendarEvent> {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback<EventPropGetter<CalendarEvent>>(
      (event, _start, _end, _isSelected) => {
        if (event.data.groupId === NO_GROUP_ID) {
          event.title = <div>{event.data.name}</div>;
          return {
            className: "rbc-event--extra",
          };
        }
        const allocationStatus =
          registration.groups[event.data.groupId]?.allocationStatus;

        if (!isDefined(allocationStatus)) {
          return {};
        }

        const classNames = [];
        switch (allocationStatus) {
          case "ALLOCATED_TO_THIS": {
            classNames.push(
              "rbc-event--base--filled",
              "rbc-event__border",
              "rbc-event__border--green"
            );
            break;
          }
          case "ALLOCATED_TO_OTHER": {
            classNames.push(
              "rbc-event--base",
              "rbc-event__border",
              "rbc-event__border--green"
            );

            break;
          }
          case "MULTIPLE_ALLOCATIONS":
            classNames.push(
              "rbc-event--base--error",
              "rbc-event__border",
              "rbc-event__border--red"
            );
            break;
          case "NOT_ALLOCATED":
            classNames.push("rbc-event--other");
            break;
        }
        allocationStatus satisfies AllocationStatus;

        event.title = <div>{event.data.name}</div>;
        return {
          className: cn(...classNames),
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [registration]
    );
  }

  function handleErrorSuppression(): void {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMemo(() => {
      suppressError();
    }, []);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      suppressError();
      return () => {
        unSuppressError();
      };
    }, []);
  }

  function intervalDates(): [Date, Date] {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(() => {
      const startInterval = dayjs(dateInterval.min.local).startOf("w").toDate();
      const endInterval = dayjs(dateInterval.max.local).endOf("w").toDate();

      return [startInterval, endInterval];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateInterval]);
  }

  function createDefaultComponents(): Components<CalendarEvent, object> {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo((): Components<CalendarEvent> => {
      return {
        header: (props) => <CalendarHeader {...props} isDesktop={isDesktop} />,
        toolbar: (props) => (
          <CalendarToolbar
            {...props}
            setDisplayState={setDisplayState}
            displayState={displayState}
            dateInterval={dateInterval}
            translations={translations}
            analyticsEvent={analyticsEvent}
          />
        ),
        eventWrapper: (props) => {
          // props has children and other properties which rbc types do not represent
          const wrapperProps = props as EventWrapperProps;
          return <EventWrapper {...wrapperProps} />;
        },
        agenda: {
          event: (e) => <div>{e.title}</div>,
        },
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateInterval, isDesktop]);
  }

  function createUniqueEvents(): SetEventTimesReturn[] {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(() => {
      if (eventsWithinSpan?.length === 0) {
        return [];
      }

      const events = makeUniqueEvents(eventsWithinSpan ?? []);

      const year = dayjs(defaultCalendarDate).year();
      const week = dayjs(defaultCalendarDate).week();
      const timeMatchedEvents = setEventTimes({
        year,
        week,
        events,
      });

      setMinMaxHours(({ minHours, maxHours }) => {
        const { min, max } = eventsMinMaxHourRange({
          events: timeMatchedEvents,
          minHours,
          maxHours,
        });

        if (minHours !== min || maxHours !== max) {
          return { minHours: min, maxHours: max };
        }
        return { minHours, maxHours };
      });

      return timeMatchedEvents;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventsWithinSpan]);
  }

  function eventReservations(): CalendarEvent[] {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(() => {
      const filteredReservations = (registration.events ?? []).filter(
        (event) => {
          if (!isDefined(event)) return false;
          if (event.groupId === NO_GROUP_ID) return true;
          return (
            registration.groups[event.groupId]?.allocationStatus ===
              "ALLOCATED_TO_THIS" ||
            registration.groups[event.groupId]?.allocationStatus ===
              "MULTIPLE_ALLOCATIONS"
          );
        }
      );
      const reservationEvents = createEvents({
        reservations: [...filteredReservations, ...extraReservations].filter(
          isDefined
        ),
      });

      // Important that extraEvents is passed in first
      // Otherwise if there is a identical event in extraEvents and reservationEvents
      // later when we filter on uniqueness the event from extraEvent will be removed
      // Which will change the order in the calendar
      // Then reserved event will always be first in calendar GUI(to the right)
      return [...extraEvents, ...reservationEvents];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      registration.events,
      registration.groups,
      props.extraEvents,
      props.extraReservations,
    ]);
  }

  function findEventsWithinSpan(): CalendarEvent[] {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(() => {
      const studentSetGroupIds =
        registration.activities[activityId ?? UNKNOWN_ID]?.children ?? [];
      return (
        reservationEvents.filter(({ data, noSchedule, start, end }) => {
          if (noSchedule) return studentSetGroupIds.includes(data.groupId);
          return (
            dayjs(start).isSameOrAfter(startIntervalDate) &&
            dayjs(end).isSameOrBefore(endIntervalDate)
          );
        }) ?? []
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      reservationEvents,
      startIntervalDate,
      endIntervalDate,
      activityId,
      registration.activities,
    ]);
  }

  function createDefaultOnSelectEvent(): (event: CalendarEvent) => void {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback((event: CalendarEvent) => {
      eventModal.setShowModal(true);
      eventModal.setModalInfo(event);
    }, []);
  }

  function createDefaultFormats(): Formats {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo((): Formats => {
      return {
        agendaDateFormat: (date) => {
          return dayjs(date).format("dddd");
        },
        timeGutterFormat: (date) => {
          // TODO: Support AM and PM formats?
          return dayjs(date).format("HH");
        },
      };
    }, []);
  }

  function createHourDate(hour: number): Date {
    const date = new Date(defaultHourDate);
    date.setHours(hour, 0, 0, 0);
    return date;
  }
}

interface AdjustCalendarHourRange {
  events: CalendarEvent[];
  maxHours: number;
  minHours: number;
}
/**
 * Change the calendar hour range to match earliest and latest event if they are before or after the default range.
 * @param events The calendar events based on reservations
 * @param maxHours Initial max hour range
 * @param minHours Initial min hour range
 */
type EventsMinMaxHourRangeReturn = { min: number; max: number };
export function eventsMinMaxHourRange({
  events,
  maxHours,
  minHours,
}: AdjustCalendarHourRange): EventsMinMaxHourRangeReturn {
  let latestHour = maxHours;
  let earliestHour = minHours;

  events.forEach((event) => {
    if (event.noSchedule) return;
    const beginDate = event.start;
    const endDate = event.end;
    if (beginDate === undefined || endDate === undefined) {
      return;
    }

    if (beginDate.getHours() <= earliestHour) {
      earliestHour = beginDate.getHours();
    }

    if (endDate.getHours() >= latestHour) {
      latestHour = endDate.getHours();
      const endDateMin = endDate.getMinutes();
      const endDateSec = endDate.getSeconds();
      if (endDateMin > 0 || endDateSec > 0) {
        latestHour++;
      }
    }
  });

  // The calendar min max range can not go all the way to hour 24,
  // or it will result in an invalid array length inside react big calendar.
  return {
    min: limitedToRange(earliestHour, 0, latestHour),
    max: limitedToRange(latestHour, earliestHour, 23),
  };
}

/**
 *
 * @param events An array of events for calendar
 * @returns A unique list of events based on day and hour time, that contains the weeks when the duplicated events are scheduled.
 */
export function makeUniqueEvents(events: CalendarEvent[]): CalendarEvent[] {
  return events.reduce<CalendarEvent[]>((savedEvents, event) => {
    const beginDate = event.start;
    const endDate = event.end;
    const eventName = event.data?.name;

    if (!isDefined(beginDate) || !isDefined(endDate) || !isDefined(eventName)) {
      return savedEvents;
    }

    const existingEvent = savedEvents.find((savedEvent) => {
      const savedBeginDate = savedEvent.start;
      const savedEndDate = savedEvent.end;
      const savedEventName = savedEvent.data?.name;

      const hasDates = isDefined(savedBeginDate) && isDefined(savedEndDate);
      return (
        hasDates &&
        isDefined(savedEventName) &&
        shouldMergeEvents({ event1: savedEvent, event2: event })
      );
    });

    if (existingEvent === undefined) {
      const day = dayjs(beginDate).startOf("day").unix();
      const newEvent: CalendarEvent = {
        ...event,
        data: {
          ...event.data,
          days: new Set(event.data.days).add(day),
        },
      };
      return [...savedEvents, newEvent];
    }

    existingEvent.data.reservations = mergeById(
      existingEvent.data.reservations,
      event.data.reservations
    );

    if (!existingEvent.data.hasConflict) {
      existingEvent.data.hasConflict = event.data.hasConflict;
    }

    const day = dayjs(beginDate).startOf("day").unix();
    existingEvent.data.days.add(day);

    return savedEvents;
  }, []);
}

interface ShouldMergeEvent {
  event1: CalendarEvent;
  event2: CalendarEvent;
}
/**
 *
 * @param event1 First event to compare
 * @param event2 Second event to compare
 * @returns A boolean whether the two events are the same. It will not take week into account, only hour and day
 */
export function shouldMergeEvents({
  event1,
  event2,
}: ShouldMergeEvent): boolean {
  return (
    hasSameTime(
      { beginDate: event1.start, endDate: event1.end },
      { beginDate: event2.start, endDate: event2.end }
    ) &&
    event1.data.name === event2.data.name &&
    event1.data.groupId === event2.data.groupId
  );
}

interface SetEventTimes {
  events: CalendarEvent[];
  week: number;
  year: number;
}
/**
 *
 * @param events Events
 * @param The week the event should be set to
 * @param The year the event should be set to
 * @returns A event list with a begin and end value that matches time parameters.
 */
interface SetEventTimesReturn extends CalendarEvent {
  start: Date;
  end: Date;
}
export function setEventTimes({
  year,
  week,
  events,
}: SetEventTimes): SetEventTimesReturn[] {
  const firstDayOfWeek = dayjs.localeData().firstDayOfWeek();
  // Start with the beginning of the target week
  const weekStart = dayjs().year(year).week(week).startOf("week");

  return events.map((event) => {
    const startDay = dayjs(event.start).day();
    const endDay = dayjs(event.end).day();

    // Offset from locale's first day of the week
    // If monday is the first day of the week (1) and the event starts on sunday (0)
    // Then we need to wrap around and set it to day 6.
    const adjustedStartDay = (startDay - firstDayOfWeek + 7) % 7;
    const adjustedEndDay = (endDay - firstDayOfWeek + 7) % 7;

    const start = weekStart
      .add(adjustedStartDay, "day")
      .year(year)
      .hour(dayjs(event.start).hour())
      .minute(dayjs(event.start).minute())
      .second(dayjs(event.start).second())
      .millisecond(0)
      .toDate();

    const end = weekStart
      .add(adjustedEndDay, "day")
      .year(year)
      .hour(dayjs(event.end).hour())
      .minute(dayjs(event.end).minute())
      .second(dayjs(event.end).second())
      .millisecond(0)
      .toDate();

    return {
      ...event,
      start,
      end,
    };
  });
}

interface StartEndDate {
  beginDate: Date;
  endDate: Date;
}
/**
 *
 * @param a First start end time object
 * @param b Second start end time object
 * @returns A boolean based on day and hour are the same of the time objects. It also checks for the duration to be the same
 */
function hasSameTime(a: StartEndDate, b: StartEndDate): boolean {
  return (
    dayjs(a.beginDate).format("dd HH:mm") ===
      dayjs(b.beginDate).format("dd HH:mm") &&
    dayjs(a.endDate).format("dd HH:mm") ===
      dayjs(b.endDate).format("dd HH:mm") &&
    dayjs(a.beginDate).diff(a.endDate) === dayjs(b.beginDate).diff(b.endDate)
  );
}

/**
 * Suppress specific error from which comes from react-big-calendar
 */
type ErrorSuppressionReturn = {
  suppressError: () => void;
  unSuppressError: () => void;
};
export function errorSuppression(
  originalError: Console["error"]
): ErrorSuppressionReturn {
  return {
    suppressError: () => {
      /* eslint-disable-next-line no-console */
      console.error = function (...args) {
        if (
          args[0].includes?.(
            "Using UNSAFE_componentWillReceiveProps in strict mode"
          )
        ) {
          return;
        }
        originalError.apply(console, args);
      };
    },
    unSuppressError: () => {
      /* eslint-disable-next-line no-console */
      console.error = originalError;
    },
  };
}

type CreateEventsOverlappingProps = {
  events: CalendarEvent[];
};

/**
 * @param events - A list of events that checks if any overlaps
 * @returns A record with event id as key and a list of events that overlaps with the key
 * example - {id1: [id2, id3], id2: [id1], id3: [id1]}
 */
export function createEventsOverlapping({
  events,
}: CreateEventsOverlappingProps): UnsafeRecord<string, CalendarEvent[]> {
  return events.reduce<UnsafeRecord<string, CalendarEvent[]>>(
    (eventsOverlapping, eventToCheck, index, eventsArray) => {
      // Last index should already have all overlaps.
      const checkFromIndex = index + 1;
      if (checkFromIndex === eventsArray.length) return eventsOverlapping;

      const remainderToCheck = eventsArray.slice(checkFromIndex);
      remainderToCheck.forEach((event) => {
        if (eventsHasOverlap(eventToCheck, event)) {
          const currentEventIds1 = eventsOverlapping[eventToCheck.id] ?? [];
          eventsOverlapping[eventToCheck.id] = [...currentEventIds1, event];

          const currentEventIds2 = eventsOverlapping[event.id] ?? [];
          eventsOverlapping[event.id] = [...currentEventIds2, eventToCheck];
        }
      });

      return eventsOverlapping;
    },
    {}
  );
}

function eventsHasOverlap(
  event1: CalendarEvent,
  event2: CalendarEvent
): boolean {
  return (
    dayjs(event1.start).isBefore(event2.end) &&
    dayjs(event1.end).isAfter(event2.start)
  );
}
